import { updateWalkthrough } from "@/hooks/react-query/use-walkthrough";
import { isPublicRoute } from "@/lib/routes/public-routes";
import { supabase } from "@/lib/supabase";
import { walkthroughInitFlags } from "@/lib/walkthrough-defaults";
import type { Session } from "@supabase/supabase-js";
import type { MutableRefObject } from "react";
import { isMobileDevice, redirectWithSignOut } from "./utils";

// Post-login navigation used both by signIn (direct call) and by the auth
// state listener (when SIGNED_IN fires from a non-auth, non-dashboard route).
// The ref-based mutex prevents duplicate navigation when both entry points
// race; the try/finally guarantees it is released even on error paths.
export async function runPostLoginNavigation(
  session: Session,
  navigationInProgressRef: MutableRefObject<boolean>,
  options?: { ignorePublicRouteGuard?: boolean },
): Promise<void> {
  if (navigationInProgressRef.current) return;
  navigationInProgressRef.current = true;

  try {
    if (typeof window === "undefined") return;

    const userMetadata = session.user.user_metadata || {};
    const userType =
      typeof userMetadata.user_type === "string"
        ? userMetadata.user_type
        : undefined;
    const currentPath = window.location.pathname;

    // Do not redirect out of pages that own their own flow.
    if (currentPath.includes("/onboarding/")) return;
    if (currentPath.includes("/landing/thankyou")) return;
    // Source of truth: src/lib/routes/public-routes.ts — shared with the
    // edge middleware so the two guards never disagree. Callers that just
    // adopted a fresh session while sitting ON a public route (e.g. the
    // public job-apply flow after signup-with-application) pass
    // `ignorePublicRouteGuard` so the login still routes to the dashboard.
    if (!options?.ignorePublicRouteGuard && isPublicRoute(currentPath)) return;
    // Already on a dashboard (and not coming from an auth page) — stay put.
    if (
      !currentPath.includes("/auth/") &&
      (currentPath.startsWith("/company-dashboard") ||
        currentPath.startsWith("/jobber-dashboard"))
    ) {
      return;
    }

    if (!userType) {
      window.location.href = "/onboarding/user-type";
      return;
    }

    if (userType === "jobber") {
      if (isMobileDevice()) {
        await redirectWithSignOut("/landing/desktop-only");
        return;
      }
      // The jobber dashboard handles profile completeness checks and
      // redirects to onboarding if needed.
      window.location.href = "/jobber-dashboard";
      return;
    }

    if (userType === "company" || userType === "client") {
      if (isMobileDevice()) {
        await redirectWithSignOut("/landing/desktop-only");
        return;
      }

      const fullName = userMetadata.full_name as string | null | undefined;
      if (!fullName || fullName.trim() === "") {
        window.location.href = "/onboarding/company";
        return;
      }

      // Initialize walkthrough flags BEFORE loading dashboard (companies only).
      if (!userMetadata.walkthrough) {
        try {
          const userId = session.user?.id;
          if (userId) {
            await Promise.all(
              walkthroughInitFlags.map((flag) =>
                updateWalkthrough(userId, flag),
              ),
            );
            await supabase.auth.refreshSession();
          }
        } catch (error) {
          // Non-critical: walkthrough initialization failed
        }
      }

      window.location.href = "/company-dashboard";
    }
  } finally {
    navigationInProgressRef.current = false;
  }
}
